import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { useDispatch, connect, ConnectedProps, useSelector } from 'react-redux';
import { Checkbox } from 'antd';
import { Field as FieldReact } from 'react-final-form';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import { IRegisterRequest } from 'Data/interfaces/Auth/IRegisterRequest';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { TenantActions } from 'Store/Tenant/Tenant.actions';
import { getDomain } from 'Store/MultiDomain/MultiDomain.selector';
import { AuthActions } from 'Store/Auth/Auth.actions';
import BoxContainer from 'Components/UI/Containers/BoxContainer/BoxContainer';
import Button from 'Components/UI/Button';
import Form from 'Components/UI/Form/Form';
import Field from 'Components/UI/Field';
import FloatingFeedback from 'Components/UI/Input/TextInput/FloatingFeedback/FloatingFeedback';
import GoogleLinkButton from 'Components/UI/GoogleLinkButton';
import Select from 'Components/UI/Select';
import Link from 'Components/UI/Link';
import Background from '../Backgroud/background';
import schema, { schemaPassword } from './signup.schema';
import styles from './Signup.module.scss';

export interface ISignup {
  match: any;
}

const Signup: FC<Props> = (props) => {
  const { t } = useTranslation(['auth', 'common']);
  const termsOfUseUrlFlag = useFeatureFlag('terms-of-use-url');
  const termsOfUseUrl = (termsOfUseUrlFlag.enabled && typeof termsOfUseUrlFlag.value === 'string')
    ? termsOfUseUrlFlag.value
    : (process.env.REACT_APP_TERMS_OF_USE_URL || '');
  const privacyPolicesUrlFlag = useFeatureFlag('privacy-policies-url');
  const privacyPolicesUrl = (privacyPolicesUrlFlag.enabled && typeof privacyPolicesUrlFlag.value === 'string')
    ? privacyPolicesUrlFlag.value
    : (process.env.REACT_APP_PRIVACY_POLICIES_URL || '');

  const dispatch = useDispatch();
  
  const domain = useSelector(getDomain);
  const [isPositionCompany, setPositionCompany] = useState<any>();
  const [errorPosition, setErrorPosition] = useState<string | undefined>(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    dispatch(AuthActions.listCompanyPosition());
  }, [dispatch]);

  useEffect(() => {
    if (isPositionCompany) { setErrorPosition(undefined); }
  }, [isPositionCompany]);

  const companyPositions = props.listCompanyPosition?.map((role) => ({
    value: role.CompanyPositionId,
    description: role.Name,
    label: role.Name,
  }));

  const signup = (data: IRegisterRequest) => {
    if (!isPositionCompany) {
      setErrorPosition(t('common:validation.fieldRequiredSelect') as string);
      return;
    }

    if (props.tenantInviteInfo?.EmailHash) {
      data.tenantInviteEmailHash = props.tenantInviteInfo.EmailHash;
    }
    data.companyPositionId = isPositionCompany;
    dispatch(AuthActions.userRegister(data));
  };

  const [password, setPassword] = useState<string | undefined>(undefined);
  const [passwordFocus, setPasswordFocus] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<string | undefined>(
    undefined,
  );
  const [confirmPasswordFocus, setConfirmPasswordFocus] = useState<boolean>(false);

  const location = useLocation();
  const params = useParams();
  const redirectUrl = window.location.origin + location.pathname + location.search;

  const validField = (meta: any) => {
    return !meta.submitFailed ? 'default' : meta.valid ? 'valid' : 'error';
  };

  useEffect(() => {
    if (params.emailHash) {
      dispatch(TenantActions.getTenantInviteInfo(params.emailHash));
    }
  }, [dispatch, params.emailHash]);

  return (
    <Background>
      <BoxContainer height={domain?.LogoUrl ? 646 : 636} width={372}>
        <div className={styles['contentWrapper']}>
          <h2 className={styles['title']}>{t('signup.title')}</h2>
          <Form schema={schema} onSubmit={signup}>
            <Field
              name='nome'
              label={t('common:name')}
              value={props.tenantInviteInfo?.InvitedName}
              icon='menuGerenciarUsuario'
              placeholder={t('signup.namePlaceholder')}
            />
            <Select
              onSelect={setPositionCompany}
              className={styles['selectPositionCompany']}
              iconClassName={styles['selectIcon']}
              placeholder={t('signup.jobPlaceholder')}
              options={companyPositions}
              iconInput='buscar'
              size='large'
              error={errorPosition}
            />
            <Field
              className={styles['emailCad']}
              name='email'
              label={t('common:email')}
              icon='arroba'
              disabled={!!params.emailHash}
              value={props.tenantInviteInfo?.InvitedEmail}
              placeholder={t('signup.emailPlaceholder')}
            />
            <Field
              name='password'
              label={t('common:password')}
              icon='cadeado'
              type={showPassword ? 'text' : 'password'}
              rightIcon={showPassword ? 'notVisualizar' : 'visualizar'}
              rightIconSize={12}
              onRightIconClick={() => setShowPassword((prev) => !prev)}
              onInput={setPassword}
              onBlur={() => setPasswordFocus(false)}
              onFocus={() => setPasswordFocus(true)}
            />
            <FloatingFeedback
              name='password'
              schema={schemaPassword}
              inputValue={password}
              visible={passwordFocus}
              className={styles['passwordFeedback']}
            />
            <Field
              name='confirmPassword'
              label={t('signup.confirmPassword')}
              icon='cadeado'
              type={showConfirmPassword ? 'text' : 'password'}
              rightIcon={showConfirmPassword ? 'notVisualizar' : 'visualizar'}
              rightIconSize={12}
              onRightIconClick={() => setShowConfirmPassword((prev) => !prev)}
              onInput={setConfirmPassword}
              onBlur={() => setConfirmPasswordFocus(false)}
              onFocus={() => setConfirmPasswordFocus(true)}
            />
            <FloatingFeedback
              name='confirmPassword'
              schema={schemaPassword}
              inputValue={confirmPassword}
              visible={confirmPasswordFocus}
              className={styles['confirmPasswordFeedback']}
              context={{ password }}
            />
            <FieldReact name='isTermsAccepted' type='checkbox'>
              {(propsField) => (
                <Checkbox
                  className={`${styles['checkbox']}
                                        ${styles[validField(propsField.meta)]}`}
                  {...propsField.input}
                >
                  <span>{t('signup.termsDeclaration')} </span>
                  <Link
                    to={termsOfUseUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    isExternal={true}
                  >
                    {t('signup.termsOfUse')}
                  </Link>
                  <span> e as </span>
                  <Link
                    to={privacyPolicesUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    isExternal={true}
                  >
                    {t('signup.privacyPolicy')}
                  </Link>
                </Checkbox>
              )}
            </FieldReact>
            <Button
              width='100%'
              type='primary'
              htmlType='submit'
              loading={props.isLoading}
              disabled={props.isLoading}
            >
              {t('signup.createAccount')}
            </Button>
          </Form>
          <GoogleLinkButton
            emailHash={params.emailHash}
            redirectUrl={redirectUrl}
          />
          <div className={styles['hasAccount']}>
            <span>
              {t('common.alreadyHaveAccount')}&nbsp;
              <Link
                to={`/login${params.emailHash ? `/${params.emailHash}` : ''}`}
              >
                {t('common.loginNow')}
              </Link>
            </span>
          </div>
          {domain?.LogoUrl && (
            <p className={styles['powered-by']}>
              Powered by <span>Coordly</span>
            </p>
          )}
        </div>
      </BoxContainer>
    </Background>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.auth,
  tenantInviteInfo: state.tenant.TenantInviteInfo,
});

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector>;

export default connector(Signup);
